import { Component, DestroyRef, DOCUMENT, inject, type OnInit, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import {
    ActivatedRoute,
    ActivationStart,
    NavigationEnd,
    Router,
    RouterModule,
} from '@angular/router';
import { NgxLoadingBar } from '@ngx-loading-bar/core';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { ConfirmPopupModule } from '@openng/optimus-ui/confirmpopup';
import { ToastModule } from '@openng/optimus-ui/toast';
import { filter, take } from 'rxjs';

import { ConnectionService } from '@app/connection.service';
import { AppConfigService } from '@app/shared/api/app-config.service';
import { GoogleAnalyticsService } from '@app/shared/browser/google-analytics.service';
import { BreadcrumbService } from '@app/shared/routing/breadcrumb.service';
import { AppConfigErrorDialogComponent } from '@app/theme/app-config-error-dialog/app-config-error-dialog.component';
import { ChunkNotFoundDialogComponent } from '@app/theme/chunk-not-found-dialog/chunk-not-found-dialog.component';
import { DatabaseMigrationService } from '@app/theme/database-migration-modal/database-migration.service';
import { DatabaseMigrationModalComponent } from '@app/theme/database-migration-modal/database-migration-modal.component';

@Component({
    selector: 'cor-app',
    template: `
        @if (!hasInternetConnection) {
          <div class="connection-status tw:bg-red-600 tw:text-surface-0">{{ internetStatus }}</div>
        }

        <!-- begin:: Page -->
        <router-outlet />
        <router-outlet name="settings" />
        <ngx-loading-bar
          [includeSpinner]="false"
          height="0.2rem"
          class="loading-bar" />
        <!-- end:: Page -->

        <!-- Chunk Not Found Dialog - Always available for error handling -->
        <cor-chunk-not-found-dialog />

        <!-- App Config Error Dialog - Always available for error handling -->
        <cor-app-config-error-dialog />

        <!-- Database Migration Modal - Always available, shows when migrations are pending -->
        @if (migrationService.visible()) {
          <cor-database-migration-modal />
        }

        <p-confirmdialog key="confirm-dialog" />
        <p-confirmpopup key="confirm-popup" />
        <p-toast preventOpenDuplicates />
    `,
    styleUrl: './app.component.scss',
    standalone: true,
    imports: [
        RouterModule,
        NgxLoadingBar,
        ChunkNotFoundDialogComponent,
        AppConfigErrorDialogComponent,
        DatabaseMigrationModalComponent,
        ConfirmDialogModule,
        ConfirmPopupModule,
        ToastModule,
    ],
})
export class AppComponent implements OnInit {
    protected hasInternetConnection: boolean;
    protected internetStatus: string;

    private readonly titleService = inject(Title);
    private readonly breadcrumbService = inject(BreadcrumbService);
    private readonly connection = inject(ConnectionService);
    private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly renderer = inject(Renderer2);
    private readonly document = inject(DOCUMENT);
    private readonly destroyRef = inject(DestroyRef);
    private readonly appConfigService = inject(AppConfigService);
    protected readonly migrationService = inject(DatabaseMigrationService);

    constructor() {
        this.googleAnalyticsService.loadGTag();

        this.connection
            .monitor()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(isConnected => {
                this.hasInternetConnection = isConnected;
                this.internetStatus = this.hasInternetConnection
                    ? 'ONLINE'
                    : "You're offline. Please check the internet connection";
            });
    }

    ngOnInit() {
        // After the page loads (i.e. the first angular routing finishes) - remove the splash screen loader
        // This way, the next time index.html is loaded (i.e. full page refresh occurs) - the loader will show up
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                take(1),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                const splashElement = this.document.getElementById('splashscreen');
                if (splashElement) {
                    this.renderer.setStyle(splashElement, 'display', 'none');
                }
            });

        this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.breadcrumbService.generateBreadcrumbData(this.route.root);
            }
        });

        this.setTitle();
        this.checkMigrationStatus();
    }

    private checkMigrationStatus(): void {
        // Check if migrations are pending
        this.appConfigService.appConfig$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(config => {
                if (config.pendingMigration) {
                    this.migrationService.visible.set(true);
                }
            });
    }

    private setTitle() {
        this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
            if (event instanceof ActivationStart) {
                const { data } = event.snapshot;
                if (
                    Object.entries(data).length > 0 &&
                    data.constructor === Object &&
                    Object.hasOwn(data, 'title')
                ) {
                    const subtitle = data.title || 'GenGuardX';
                    this.titleService.setTitle(subtitle);
                }
            }
        });
    }
}
